"use client";

import { useCallback } from "react";
import { toast } from "@/hooks/useToast";
import type { TabItem, RowItem, ColumnItem, ValueItem, VariantDetails, Form6Config, DEFAULT_CONFIG } from "../types";

interface UseDataOperationsProps {
  parentId: number | null;
  tabs: TabItem[];
  rows: RowItem[];
  columns: ColumnItem[];
  values: ValueItem[];
  currentUserId?: number;
  loadData: () => Promise<void>;
  variantDetails?: VariantDetails;
  config: Form6Config;
}

export const useDataOperations = ({
  parentId,
  tabs,
  rows,
  columns,
  values,
  currentUserId,
  loadData,
  variantDetails,
  config,
}: UseDataOperationsProps) => {
  // API methods from variant details
  const createTabMethod = variantDetails?.createTabMethod;
  const updateTabMethod = variantDetails?.updateTabMethod;
  const deleteTabMethod = variantDetails?.deleteTabMethod;
  const createRowMethod = variantDetails?.createRowMethod;
  const updateRowMethod = variantDetails?.updateRowMethod;
  const deleteRowMethod = variantDetails?.deleteRowMethod;
  const createColumnMethod = variantDetails?.createColumnMethod;
  const updateColumnMethod = variantDetails?.updateColumnMethod;
  const deleteColumnMethod = variantDetails?.deleteColumnMethod;
  const createValueMethod = variantDetails?.createValueMethod;
  const updateValueMethod = variantDetails?.updateValueMethod;

  // Helper: Get rows/columns for a specific tab
  const getRowsForTab = useCallback(
    (tabId: number): RowItem[] => {
      // Check if rows have tab_id or fee_type_id (backward compatibility)
      const hasTabFiltering = rows.some((r) => r.tab_id !== undefined || r.fee_type_id !== undefined);
      if (hasTabFiltering) {
        return rows.filter((r) => (r.tab_id || r.fee_type_id) === tabId);
      }
      return rows;
    },
    [rows]
  );

  const getColumnsForTab = useCallback(
    (tabId: number): ColumnItem[] => {
      // Check if columns have tab_id or fee_type_id (backward compatibility)
      const hasTabFiltering = columns.some((c) => c.tab_id !== undefined || c.fee_type_id !== undefined);
      if (hasTabFiltering) {
        return columns.filter((c) => (c.tab_id || c.fee_type_id) === tabId);
      }
      return columns;
    },
    [columns]
  );

  const getValue = useCallback(
    (tabId: number, rowId: number, colId: number): ValueItem | null => {
      return (
        values.find(
          (v) =>
            (v.tab_id || v.fee_type_id) === tabId &&
            (v.row_id || v.category_id) === rowId &&
            (v.column_id || v.semester_id) === colId
        ) || null
      );
    },
    [values]
  );

  // ============================================================================
  // Tab Operations
  // ============================================================================
  const handleAddTab = useCallback(async () => {
    if (!createTabMethod || !parentId) return;

    try {
      const newTab = {
        name: `${config.tabsSingularLabel} ${tabs.length + 1}`,
        order_index: tabs.length + 1,
        is_active: true,
        program_fee_id: parentId, // backward compatibility
        created_by: currentUserId,
      };
      await createTabMethod(newTab);
      toast({ title: "Success", description: `${config.tabsSingularLabel} added` });
      await loadData();
    } catch (error) {
      toast({ title: "Error", description: `Failed to add ${config.tabsSingularLabel?.toLowerCase()}`, variant: "destructive" });
    }
  }, [createTabMethod, parentId, tabs.length, currentUserId, loadData, config]);

  // Note: Confirmation should be handled by caller using DeleteDialog
  const handleDeleteTab = useCallback(
    async (tabId: number) => {
      if (!deleteTabMethod) return;

      try {
        await deleteTabMethod(tabId);
        toast({ title: "Success", description: `${config.tabsSingularLabel} deleted` });
        await loadData();
        return true;
      } catch (error) {
        toast({ title: "Error", description: `Failed to delete ${config.tabsSingularLabel?.toLowerCase()}`, variant: "destructive" });
        return false;
      }
    },
    [deleteTabMethod, loadData, config]
  );

  // Helper function to get delete confirmation message for tab
  const getDeleteTabConfirmation = useCallback(
    () => `Delete this ${config.tabsSingularLabel?.toLowerCase()} and all its data?`,
    [config]
  );

  const handleUpdateTabName = useCallback(
    async (tabId: number, newName: string) => {
      if (!updateTabMethod) return;

      try {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
          await updateTabMethod(tabId, { ...tab, name: newName, updated_by: currentUserId });
          await loadData();
        }
      } catch (error) {
        toast({ title: "Error", description: `Failed to update ${config.tabsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [updateTabMethod, tabs, currentUserId, loadData, config]
  );

  const handleUpdateTabStyle = useCallback(
    async (
      tabId: number,
      name: string,
      textColor?: string,
      bgColor?: string,
      activeTextColor?: string,
      activeBgColor?: string
    ) => {
      if (!updateTabMethod) return;

      try {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
          await updateTabMethod(tabId, {
            ...tab,
            name,
            text_color: textColor || null,
            bg_color: bgColor || null,
            active_text_color: activeTextColor || null,
            active_bg_color: activeBgColor || null,
            updated_by: currentUserId,
          });
          await loadData();
          toast({ title: "Success", description: `${config.tabsSingularLabel} updated` });
        }
      } catch (error) {
        toast({ title: "Error", description: `Failed to update ${config.tabsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [updateTabMethod, tabs, currentUserId, loadData, config]
  );

  const handleUpdateCategoryHeader = useCallback(
    async (
      tabId: number,
      headerValue: string,
      textColor: string,
      bgColor: string,
      borderColor: string
    ) => {
      if (!updateTabMethod) return;

      try {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
          await updateTabMethod(tabId, {
            ...tab,
            category_header: headerValue || config.categoryHeaderLabel,
            category_header_text_color: textColor || null,
            category_header_bg_color: bgColor || null,
            category_header_border_color: borderColor || null,
            updated_by: currentUserId,
          });
          await loadData();
          toast({ title: "Success", description: "Header updated" });
        }
      } catch (error) {
        toast({ title: "Error", description: "Failed to update header", variant: "destructive" });
      }
    },
    [updateTabMethod, tabs, currentUserId, loadData, config]
  );

  // ============================================================================
  // Row Operations
  // ============================================================================
  const handleAddRow = useCallback(
    async (tabId: number) => {
      if (!createRowMethod || !parentId) return;

      const tabRows = getRowsForTab(tabId);
      try {
        const newRow = {
          name: `${config.rowsSingularLabel} ${tabRows.length + 1}`,
          order_index: tabRows.length + 1,
          is_active: true,
          program_fee_id: parentId, // backward compatibility
          fee_type_id: tabId, // backward compatibility
          tab_id: tabId,
          created_by: currentUserId,
        };
        await createRowMethod(newRow);
        toast({ title: "Success", description: `${config.rowsSingularLabel} added` });
        await loadData();
      } catch (error) {
        toast({ title: "Error", description: `Failed to add ${config.rowsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [createRowMethod, parentId, getRowsForTab, currentUserId, loadData, config]
  );

  // Note: Confirmation should be handled by caller using DeleteDialog
  const handleDeleteRow = useCallback(
    async (rowId: number) => {
      if (!deleteRowMethod) return;

      try {
        await deleteRowMethod(rowId);
        toast({ title: "Success", description: `${config.rowsSingularLabel} deleted` });
        await loadData();
      } catch (error) {
        toast({ title: "Error", description: `Failed to delete ${config.rowsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [deleteRowMethod, loadData, config]
  );

  // Helper function to get delete confirmation message for row
  const getDeleteRowConfirmation = useCallback(
    () => `Delete this ${config.rowsSingularLabel?.toLowerCase()} and all its values?`,
    [config]
  );

  const handleUpdateRowName = useCallback(
    async (
      rowId: number,
      newName: string,
      textColor?: string,
      bgColor?: string,
      borderColor?: string
    ) => {
      if (!updateRowMethod) return;

      try {
        const row = rows.find((r) => r.id === rowId);
        if (row) {
          await updateRowMethod(rowId, {
            ...row,
            name: newName,
            text_color: textColor || null,
            bg_color: bgColor || null,
            border_color: borderColor || null,
            updated_by: currentUserId,
          });
          await loadData();
        }
      } catch (error) {
        toast({ title: "Error", description: `Failed to update ${config.rowsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [updateRowMethod, rows, currentUserId, loadData, config]
  );

  // ============================================================================
  // Column Operations
  // ============================================================================
  const handleAddColumn = useCallback(
    async (tabId: number) => {
      if (!createColumnMethod || !parentId) return;

      const tabCols = getColumnsForTab(tabId);
      try {
        const newColumn = {
          name: `${config.columnsSingularLabel} ${tabCols.length + 1}`,
          order_index: tabCols.length + 1,
          is_active: true,
          program_fee_id: parentId, // backward compatibility
          fee_type_id: tabId, // backward compatibility
          tab_id: tabId,
          created_by: currentUserId,
        };
        await createColumnMethod(newColumn);
        toast({ title: "Success", description: `${config.columnsSingularLabel} added` });
        await loadData();
      } catch (error) {
        toast({ title: "Error", description: `Failed to add ${config.columnsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [createColumnMethod, parentId, getColumnsForTab, currentUserId, loadData, config]
  );

  // Note: Confirmation should be handled by caller using DeleteDialog
  const handleDeleteColumn = useCallback(
    async (colId: number) => {
      if (!deleteColumnMethod) return;

      try {
        await deleteColumnMethod(colId);
        toast({ title: "Success", description: `${config.columnsSingularLabel} deleted` });
        await loadData();
      } catch (error) {
        toast({ title: "Error", description: `Failed to delete ${config.columnsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [deleteColumnMethod, loadData, config]
  );

  // Helper function to get delete confirmation message for column
  const getDeleteColumnConfirmation = useCallback(
    () => `Delete this ${config.columnsSingularLabel?.toLowerCase()} and all its values?`,
    [config]
  );

  const handleUpdateColumnName = useCallback(
    async (
      colId: number,
      newName: string,
      textColor?: string,
      bgColor?: string,
      borderColor?: string
    ) => {
      if (!updateColumnMethod) return;

      try {
        const col = columns.find((c) => c.id === colId);
        if (col) {
          await updateColumnMethod(colId, {
            ...col,
            name: newName,
            text_color: textColor || null,
            bg_color: bgColor || null,
            border_color: borderColor || null,
            updated_by: currentUserId,
          });
          await loadData();
        }
      } catch (error) {
        toast({ title: "Error", description: `Failed to update ${config.columnsSingularLabel?.toLowerCase()}`, variant: "destructive" });
      }
    },
    [updateColumnMethod, columns, currentUserId, loadData, config]
  );

  // ============================================================================
  // Value/Cell Operations
  // ============================================================================
  const handleSaveCell = useCallback(
    async (
      tabId: number,
      rowId: number,
      colId: number,
      value: string,
      textColor: string,
      bgColor: string,
      borderColor: string
    ) => {
      const existingValue = getValue(tabId, rowId, colId);
      const trimmedValue = value.trim() || null;
      const text_color = textColor || null;
      const bg_color = bgColor || null;
      const border_color = borderColor || null;

      try {
        if (existingValue) {
          if (updateValueMethod) {
            await updateValueMethod(existingValue.id, {
              ...existingValue,
              value: trimmedValue,
              text_color,
              bg_color,
              border_color,
              updated_by: currentUserId,
            });
            toast({ title: "Success", description: "Value updated" });
          } else {
            toast({ title: "Error", description: "Update method not configured", variant: "destructive" });
            return false;
          }
        } else {
          if (createValueMethod && parentId) {
            await createValueMethod({
              fee_type_id: tabId, // backward compatibility
              category_id: rowId, // backward compatibility
              semester_id: colId, // backward compatibility
              tab_id: tabId,
              row_id: rowId,
              column_id: colId,
              value: trimmedValue,
              text_color,
              bg_color,
              border_color,
              is_active: true,
              program_fee_id: parentId, // backward compatibility
              created_by: currentUserId,
            });
            toast({ title: "Success", description: "Value saved" });
          } else {
            toast({ title: "Error", description: "Create method not configured", variant: "destructive" });
            return false;
          }
        }
        await loadData();
        return true;
      } catch (error: any) {
        console.error("Failed to save value:", error);
        toast({ title: "Error", description: error?.message || "Failed to save value", variant: "destructive" });
        return false;
      }
    },
    [getValue, updateValueMethod, createValueMethod, parentId, currentUserId, loadData]
  );

  return {
    // Helpers
    getRowsForTab,
    getColumnsForTab,
    getValue,
    // Tab Operations
    handleAddTab,
    handleDeleteTab,
    getDeleteTabConfirmation,
    handleUpdateTabName,
    handleUpdateTabStyle,
    handleUpdateCategoryHeader,
    // Row Operations
    handleAddRow,
    handleDeleteRow,
    getDeleteRowConfirmation,
    handleUpdateRowName,
    // Column Operations
    handleAddColumn,
    handleDeleteColumn,
    getDeleteColumnConfirmation,
    handleUpdateColumnName,
    // Cell Operations
    handleSaveCell,
  };
};

export default useDataOperations;

